import { useState, useEffect, useCallback } from 'react';
import {
  EditOutlined,
  LockOutlined,
  PlusCircleOutlined,
  RedoOutlined,
  UnlockOutlined,
} from '@ant-design/icons';
import { IAccessGroup } from '../../shared/interfaces';
import { useToast } from '../../hooks/toast';
import {
  ATable,
  DropdownOptions,
  MenuOptions,
  PageIntro,
  PageLayout,
  PagePanel,
  StatusActive,
} from '../../components';
import AccessGroupCreateEdit from './CreateEdit';
import AccessGroupPermissions from './Permissions';
import { getAccessGroups } from '../../http';
import { useAuth } from '../../hooks/auth';
import { PermissionActions } from '../../shared/enums/permissions.enum';

const AccessGroup: React.FC = () => {
  const { addToast } = useToast();
  const { permissionFind } = useAuth();
  const [search, setSearch] = useState<string>('');
  const [accessGroup, setAccessGroup] = useState<IAccessGroup | null>(null);
  const [modalCreate, setModalCreate] = useState<boolean>(false);
  const [modalPermissions, setModalPermissions] = useState<boolean>(false);
  const [accessGroups, setAccessGroups] = useState<IAccessGroup[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [limit, setLimit] = useState<number>(10);
  const [page, setPage] = useState<number>(1);
  const [pages, setPages] = useState<number>();

  const list = useCallback(async () => {
    setLoading(true);
    setAccessGroups([]);
    try {
      const response = await getAccessGroups(limit, (page - 1) * limit, search);
      setPages(response.count);
      setAccessGroups(response.data);
    } catch (err) {
      addToast({
        type: 'error',
        title: 'Erro',
        description: 'Erro ao listar os grupos de acesso',
      });
    } finally {
      setLoading(false);
    }
  }, [limit, search, page, addToast]);

  const renderModalAccessGroupCreateEdit = () => {
    if (modalCreate)
      return (
        <AccessGroupCreateEdit
          open={modalCreate}
          close={() => setModalCreate(false)}
          accessGroup={accessGroup}
          setAccessGroups={setAccessGroups}
        />
      );
  };

  const renderModalPermissions = () => {
    if (modalPermissions)
      return (
        <AccessGroupPermissions
          open={modalPermissions}
          close={() => setModalPermissions(false)}
          accessGroup={accessGroup}
        />
      );
  };

  useEffect(() => {
    list();
  }, [page, search, limit, list]);

  return (
    <PageLayout>
      <PageIntro
        badge="Cadastros"
        icon={<LockOutlined />}
        title="Grupos de acesso"
        description="Organize perfis e permissões da equipe em uma listagem alinhada ao novo padrão visual do sistema."
      />
      <MenuOptions
        menu_items={[
          {
            label: 'Atualizar',
            key: '1',
            icon: <RedoOutlined />,
            onClick: () => list(),
          },
          permissionFind([PermissionActions.accessGroupsCreate])
            ? {
                label: 'Cadastrar',
                key: '2',
                icon: <PlusCircleOutlined />,
                onClick: () => {
                  setAccessGroup(null);
                  setModalCreate(true);
                },
              }
            : null,
        ]}
        showInputSearch
        search={search}
        setSearch={setSearch}
      />
      <PagePanel>
        <ATable
          columns={[
            {
              title: 'Id',
              width: 100,
              render: (value, record, index) => <>{index + 1}</>,
            },
            {
              title: 'Descrição',
              dataIndex: 'description',
            },
            {
              title: 'Status',
              dataIndex: 'status',
              width: 95,
              render: (value: boolean) => <StatusActive status={value} />,
            },
            {
              title: 'Opções',
              width: 130,
              render: (cell: IAccessGroup) => (
                <DropdownOptions
                  items={[
                    permissionFind([PermissionActions.accessGroupsUpdate])
                      ? {
                          label: 'Editar',
                          key: '1',
                          icon: <EditOutlined />,
                          onClick: () => {
                            setAccessGroup(cell);
                            setModalCreate(true);
                          },
                        }
                      : null,
                    permissionFind([PermissionActions.accessGroupsPermissions])
                      ? {
                          label: 'Permissões',
                          key: '2',
                          icon: <UnlockOutlined />,
                          onClick: () => {
                            setAccessGroup(cell);
                            setModalPermissions(true);
                          },
                        }
                      : null,
                  ]}
                />
              ),
            },
          ]}
          loading={loading}
          dataSource={accessGroups}
          page={page}
          setPage={setPage}
          pages={pages}
          limit={limit}
          setLimit={setLimit}
        />
      </PagePanel>
      {renderModalAccessGroupCreateEdit()}
      {renderModalPermissions()}
    </PageLayout>
  );
};

export default AccessGroup;
